import React, { useState } from 'react';
import { Button, Field, Input, Modal, Note } from '../../../components/app2';
import { strapiService } from '../../../utils/api';
import AlertService from '../../../utils/alertService';

/**
 * Alta del acceso al portal para un colaborador (rediseño jul-2026).
 *
 * Misma llamada y los mismos tres desenlaces (creado, reactivado, acceso dual);
 * el molde pasa al modal del sistema. Antes era un `fixed` suelto dentro de la
 * tabla: al abrirlo desde una fila lejana quedaba fuera de la vista y no cerraba
 * con Escape.
 */

const FORM_ID = 'create-employee-access-form';

const CreateEmployeeAccessModal = ({ isOpen, onClose, employee }) => {
  const [formData, setFormData] = useState({
    email: employee?.email || ''
  });
  const [errors, setErrors] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsCreating(true);
      AlertService?.loading(
        'Creando acceso...',
        'Por favor espere mientras se crea el acceso para el empleado.'
      );

      const result = await strapiService?.users?.createEmployeeUser({
        email: formData?.email,
        employeeId: employee?.id
      });

      AlertService?.close();

      // Handle different response scenarios
      if (result?.isReactivated) {
        await AlertService?.success(
          'Usuario Reactivado',
          `Se ha reactivado el acceso para ${employee?.name}. Se ha enviado un email con las nuevas credenciales.`
        );
      } else if (result?.isEmployeeRelationAdded) {
        await AlertService?.info(
          'Acceso Dual Configurado',
          `El usuario ${employee?.name} ya tenía acceso como administrador. Ahora también puede consultar sus propios recibos de nómina.`
        );
      } else {
        await AlertService?.success(
          'Acceso Creado',
          `Se ha creado el acceso exitosamente para ${employee?.name}. Se ha enviado un email con las credenciales temporales.`
        );
      }

      // Reset form and close modal
      setFormData({ email: '' });
      setErrors({});
      onClose();

    } catch (error) {
      console.error('Error creating employee access:', error);

      AlertService?.close();

      // Handle specific errors
      const errorMessage = error?.response?.data?.error?.message || error?.message;

      if (error?.response?.status === 400) {
        if (errorMessage?.includes('already exists')) {
          await AlertService?.warning(
            'Usuario ya existe',
            'Ya existe un usuario activo con este email. No se puede crear el acceso.'
          );
        } else if (errorMessage?.includes('role not found')) {
          await AlertService?.error(
            'Error de configuración',
            'El rol de empleado no está configurado en el sistema. Contacte al administrador.'
          );
        } else {
          await AlertService?.error(
            'Datos inválidos',
            errorMessage || 'Los datos proporcionados no son válidos.'
          );
        }
      } else if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          'No tiene permisos para crear accesos de empleados.'
        );
      } else if (error?.response?.status === 404) {
        await AlertService?.error(
          'Empleado no encontrado',
          'El empleado no existe en el sistema.'
        );
      } else {
        await AlertService?.error(
          'Error al crear acceso',
          'Ocurrió un error al crear el acceso. Por favor intente nuevamente.'
        );
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <Modal
      open
      onClose={isCreating ? undefined : onClose}
      title="Crear acceso al portal"
      subtitle={employee?.name}
      maxWidth={480}
      footer={(
        <>
          <Button variant="outline" size="sm" type="button" onClick={onClose} disabled={isCreating}>
            Cancelar
          </Button>
          <Button size="sm" type="submit" form={FORM_ID} icon="key" loading={isCreating}>
            Crear acceso
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <Field
          label="Correo de acceso *"
          htmlFor="employee-access-email"
          error={errors?.email}
          hint={errors?.email ? undefined : 'Ahí llegarán las credenciales temporales.'}
        >
          <Input
            id="employee-access-email"
            type="email"
            placeholder="empleado@empresa.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            disabled={isCreating}
            required
          />
        </Field>

        <Note tone="info" icon="key" style={{ marginTop: 18 }}>
          <strong>Qué pasa al guardar:</strong> se genera una contraseña segura y se envía por
          correo. La persona tendrá que cambiarla en su primer inicio de sesión.
        </Note>
      </form>
    </Modal>
  );
};

export default CreateEmployeeAccessModal;
